import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, PieChart, Building2, DollarSign, Workflow, Megaphone, Settings, Sparkles, ChevronDown, Star, Users, Zap, MousePointer2, Plus, Minus, ExternalLink } from 'lucide-react';
import { questions } from './questions';
import type { Result } from './types';
import FAQSection from './components/FAQSection';

function MouseFollowLight() {
  return (
    <div className="pointer-events-none fixed inset-0 z-30 transition duration-300" style={{ background: 'radial-gradient(600px at var(--x, 50%) var(--y, 50%), rgba(29, 78, 216, 0.15), transparent 80%)' }} />
  );
}

function ComparisonSection() {
  return (
    <div className="relative max-w-6xl mx-auto px-4 py-16 sm:py-24 z-10" id="comparison">
      <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-50 mb-16">
        Comprehensive CRM Comparison
        <span className="sr-only"> - Detailed Analysis of Pipedrive and HubSpot</span>
      </h2>
      <p className="text-center text-slate-300 mb-12 max-w-3xl mx-auto">
        Our in-depth comparison helps businesses make an informed decision between Pipedrive and HubSpot CRM solutions. Compare features, pricing, and capabilities to find the perfect match for your needs.
      </p>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="group bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-white/10 transition-all duration-300 hover:border-blue-500/30 hover:bg-slate-800/60 hover:shadow-[0_8px_32px_-4px_rgba(59,130,246,0.2)]">
          <div className="flex items-center gap-3 mb-4">
            <Star className="w-6 h-6 text-blue-400 transition-transform duration-300 group-hover:scale-110" />
            <h3 className="text-xl font-semibold text-slate-50">Pipedrive Highlights</h3>
          </div>
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
              <span>Visual sales pipeline with intuitive drag-and-drop interface</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
              <span>Excellent for small to medium-sized sales teams</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
              <span>Strong mobile app for sales on the go</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
              <span>Industry-leading sales pipeline visualization</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
              <span>Smart email integration and tracking</span>
            </li>
          </ul>
        </div>
        <div className="group bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-white/10 transition-all duration-300 hover:border-blue-500/30 hover:bg-slate-800/60 hover:shadow-[0_8px_32px_-4px_rgba(59,130,246,0.2)]">
          <div className="flex items-center gap-3 mb-4">
            <Star className="w-6 h-6 text-blue-400 transition-transform duration-300 group-hover:scale-110" />
            <h3 className="text-xl font-semibold text-slate-50">HubSpot Highlights</h3>
          </div>
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
              <span>Comprehensive marketing automation capabilities</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
              <span>Robust free tier with essential CRM features</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
              <span>Advanced reporting and analytics tools</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
              <span>Powerful marketing automation features</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
              <span>Extensive integration ecosystem</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function PricingSection() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-16 sm:py-24 bg-slate-900/50" id="pricing">
      <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-50 mb-6">
        Pricing Comparison
        <div className="mt-2 mx-auto w-20 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
        <span className="sr-only"> - Detailed Cost Analysis</span>
      </h2>
      <p className="text-center text-slate-300 mb-16 max-w-2xl mx-auto">
        Compare Pipedrive and HubSpot pricing plans, features, and value for money. Find the most cost-effective solution for your business size and needs.
      </p>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-white/10">
          <h3 className="text-lg sm:text-xl font-semibold text-slate-50 mb-4">Pipedrive Pricing</h3>
          <div className="space-y-4">
            <div className="group p-4 bg-slate-700/30 rounded-lg transition-all duration-300 hover:bg-slate-700/50 hover:shadow-lg">
              <div className="font-semibold text-blue-300 mb-2">Essential</div>
              <div className="text-xl sm:text-2xl font-bold text-slate-50 mb-2 group-hover:text-blue-300 transition-colors duration-300">$24.00</div>
              <div className="text-sm text-slate-400">per user/month (billed annually)</div>
              <ul className="mt-3 space-y-2 text-sm text-slate-300">
                <li>• Basic CRM features</li>
                <li>• Pipeline management</li>
                <li>• Email integration</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-700/30 rounded-lg">
              <div className="font-semibold text-blue-300 mb-2">Advanced</div>
              <div className="text-2xl font-bold text-slate-50 mb-2">$49.00</div>
              <div className="text-sm text-slate-400">per user/month (billed annually)</div>
            </div>
            <div className="p-4 bg-slate-700/30 rounded-lg">
              <div className="font-semibold text-blue-300 mb-2">Professional</div>
              <div className="text-2xl font-bold text-slate-50 mb-2">$69.00</div>
              <div className="text-sm text-slate-400">per user/month (billed annually)</div>
            </div>
            <div className="p-4 bg-slate-700/30 rounded-lg">
              <div className="font-semibold text-blue-300 mb-2">Power</div>
              <div className="text-2xl font-bold text-slate-50 mb-2">$79.00</div>
              <div className="text-sm text-slate-400">per user/month (billed annually)</div>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-white/10">
          <h3 className="text-lg sm:text-xl font-semibold text-slate-50 mb-4">HubSpot Pricing</h3>
          <div className="space-y-4">
            <div className="p-4 bg-slate-700/30 rounded-lg">
              <div className="font-semibold text-blue-300 mb-2">Free</div>
              <div className="text-xl sm:text-2xl font-bold text-slate-50 mb-2">$0</div>
              <div className="text-sm text-slate-400">Free for up to 2 users</div>
              <ul className="mt-3 space-y-2 text-sm text-slate-300">
                <li>• Contact management</li>
                <li>• Deal pipeline</li>
                <li>• Email marketing tools</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-700/30 rounded-lg">
              <div className="font-semibold text-blue-300 mb-2">Starter</div>
              <div className="text-2xl font-bold text-slate-50 mb-2">$15</div>
              <div className="text-sm text-slate-400">per seat/month</div>
            </div>
            <div className="p-4 bg-slate-700/30 rounded-lg">
              <div className="font-semibold text-blue-300 mb-2">Professional</div>
              <div className="text-2xl font-bold text-slate-50 mb-2">$1,170</div>
              <div className="text-sm text-slate-400">per month (includes 5 seats)</div>
              <div className="text-xs text-slate-500 mt-1">Additional seats $45/mo</div>
            </div>
            <div className="p-4 bg-slate-700/30 rounded-lg">
              <div className="font-semibold text-blue-300 mb-2">Enterprise</div>
              <div className="text-2xl font-bold text-slate-50 mb-2">$4,300</div>
              <div className="text-sm text-slate-400">per month (includes 7 seats)</div>
              <div className="text-xs text-slate-500 mt-1">Additional seats $75/mo</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturesSection() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-16 sm:py-24" id="features">
      <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-50 mb-16">
        Key Features Comparison
        <div className="mt-2 mx-auto w-20 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
      </h2>
      <div className="grid md:grid-cols-3 gap-8">
        <div className="group bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-white/10 transition-all duration-300 hover:border-blue-500/30 hover:bg-slate-800/60 hover:shadow-[0_8px_32px_-4px_rgba(59,130,246,0.2)]">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6 text-blue-400 transition-transform duration-300 group-hover:scale-110" />
            <h3 className="text-xl font-semibold text-slate-50">Contact Management</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Contact Organization</span>
              <div className="flex gap-1">
                <span className="text-blue-400">Pipedrive</span>
                <span className="text-slate-400">4.5/5</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Contact Enrichment</span>
              <div className="flex gap-1">
                <span className="text-blue-400">HubSpot</span>
                <span className="text-slate-400">5/5</span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <Workflow className="w-6 h-6 text-blue-400" />
            <h3 className="text-xl font-semibold text-slate-50">Pipeline Management</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Visual Pipeline</span>
              <div className="flex gap-1">
                <span className="text-blue-400">Pipedrive</span>
                <span className="text-slate-400">5/5</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Deal Tracking</span>
              <div className="flex gap-1">
                <span className="text-blue-400">HubSpot</span>
                <span className="text-slate-400">4.8/5</span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="w-6 h-6 text-blue-400" />
            <h3 className="text-xl font-semibold text-slate-50">Automation</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Workflow Automation</span>
              <div className="flex gap-1">
                <span className="text-blue-400">HubSpot</span>
                <span className="text-slate-400">5/5</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Email Automation</span>
              <div className="flex gap-1">
                <span className="text-blue-400">Pipedrive</span>
                <span className="text-slate-400">4.2/5</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<Result | null>(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  
  // Page load effect
  useEffect(() => {
    setIsPageLoaded(true);
  }, []);

  // Mouse follow effect
  const handleMouseMove = (e: React.MouseEvent) => {
    const root = document.documentElement;
    root.style.setProperty('--x', `${e.clientX}px`);
    root.style.setProperty('--y', `${e.clientY}px`);
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  // Analytics tracking
  useEffect(() => {
    if (isPageLoaded) {
      // Track page view
      try {
        if (typeof gtag === 'function') {
          gtag('event', 'page_view', {
            page_title: document.title,
            page_location: window.location.href,
            page_path: window.location.pathname
          });
        }
      } catch (e) {
        console.error('Analytics error:', e);
      }
    }
  }, [isPageLoaded]);

  const calculateResult = () => {
    const scores = Object.values(answers).reduce(
      (acc, questionIndex, index) => {
        const question = questions[index];
        const option = question.options[questionIndex];
        return {
          pipedrive: acc.pipedrive + option.score.pipedrive,
          hubspot: acc.hubspot + option.score.hubspot
        };
      },
      { pipedrive: 0, hubspot: 0 }
    );

    // Calculate percentages for clearer comparison
    const total = scores.pipedrive + scores.hubspot;
    const pipedrivePercent = Math.round((scores.pipedrive / total) * 100);
    const hubspotPercent = Math.round((scores.hubspot / total) * 100);

    setResult({
      software: pipedrivePercent >= hubspotPercent ? 'pipedrive' : 'hubspot',
      score: {
        pipedrive: pipedrivePercent,
        hubspot: hubspotPercent
      }
    });

    // Track quiz completion
    try {
      if (typeof gtag === 'function') {
        gtag('event', 'quiz_completed', {
          event_category: 'Engagement',
          event_label: pipedrivePercent >= hubspotPercent ? 'Pipedrive' : 'HubSpot',
          pipedrive_score: pipedrivePercent,
          hubspot_score: hubspotPercent
        });
      }
    } catch (e) {
      console.error('Analytics error:', e);
    }
  };

  const handleAnswer = (optionIndex: number) => {
    setAnswers(prev => ({ ...prev, [questions[currentQuestion].id]: optionIndex }));
    
    // Track each answer
    try {
      if (typeof gtag === 'function') {
        gtag('event', 'question_answered', {
          event_category: 'Quiz',
          event_label: questions[currentQuestion].text,
          question_id: questions[currentQuestion].id,
          answer_selected: questions[currentQuestion].options[optionIndex].text
        });
      }
    } catch (e) {
      console.error('Analytics error:', e);
    }
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      calculateResult();
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
    
    // Track quiz reset
    try {
      if (typeof gtag === 'function') {
        gtag('event', 'quiz_reset', {
          event_category: 'Engagement'
        });
      }
    } catch (e) {
      console.error('Analytics error:', e);
    }
  };

  const getIcon = (questionId: string) => {
    switch (questionId) {
      case 'size': return <Building2 className="w-8 h-8 text-indigo-600" aria-hidden="true" />;
      case 'industry': return <PieChart className="w-8 h-8 text-indigo-600" aria-hidden="true" />;
      case 'salesCycle': return <ArrowRight className="w-8 h-8 text-indigo-600" aria-hidden="true" />;
      case 'budget': return <DollarSign className="w-8 h-8 text-indigo-600" aria-hidden="true" />;
      case 'complexity': return <Workflow className="w-8 h-8 text-indigo-600" aria-hidden="true" />;
      case 'marketing': return <Megaphone className="w-8 h-8 text-indigo-600" aria-hidden="true" />;
      case 'integration': return <Settings className="w-8 h-8 text-indigo-600" aria-hidden="true" />;
      case 'reporting': return <PieChart className="w-8 h-8 text-indigo-600" aria-hidden="true" />;
      case 'customization': return <Settings className="w-8 h-8 text-indigo-600" aria-hidden="true" />;
      default: return <PieChart className="w-8 h-8 text-indigo-600" aria-hidden="true" />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Pipedrive vs HubSpot: In-Depth CRM Comparison (2025) | Features & Pricing</title>
        <meta name="description" content="Make an informed CRM choice: Compare Pipedrive vs HubSpot pricing, features, and capabilities. Get a personalized recommendation with our interactive tool." />
        <link rel="canonical" href="https://pipedrivevshubspot.com" />
        
        {/* Preload key assets */}
        <link rel="preload" as="image" href="https://images.unsplash.com/photo-1552664730-d307ca884978" />
      </Helmet>
      <div 
        onMouseMove={handleMouseMove}
        className="min-h-screen bg-[conic-gradient(at_top_right,_var(--tw-gradient-stops))] from-slate-800 via-blue-800 to-slate-700 relative overflow-hidden"
      >
        <MouseFollowLight />
        
        {/* Mouse pointer effect */}
        <div 
          className="pointer-events-none fixed z-50 transition-transform duration-200 ease-out"
          style={{ 
            transform: `translate(${mousePosition.x - 16}px, ${mousePosition.y - 16}px)`,
            opacity: showQuiz ? 0 : 0.6
          }}
          aria-hidden="true"
        >
          <MousePointer2 className="w-8 h-8 text-blue-400/50" />
        </div>

        {/* SEO-optimized content */}
        <div className="sr-only">
          <h1>Pipedrive vs HubSpot Comparison 2025</h1>
          <p>Compare Pipedrive and HubSpot CRM features, pricing, and capabilities. Get a personalized recommendation based on your business needs.</p>
          <h2>Key Comparison Points:</h2>
          <ul>
            <li>Business Size Compatibility</li>
            <li>Pricing and Budget Considerations</li>
            <li>Industry-Specific Features</li>
            <li>Sales Cycle Management</li>
            <li>Process Complexity Handling</li>
            <li>Marketing Automation Capabilities</li>
            <li>Integration Options</li>
            <li>Reporting and Analytics</li>
            <li>Customization Possibilities</li>
          </ul>
        </div>

        {/* Header */}
        <div className="sticky top-0 left-0 right-0 p-4 text-center z-50 bg-slate-900/80 backdrop-blur-sm border-b border-white/10">
          <div className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-800/40 backdrop-blur-sm rounded-full border border-white/20 hover:border-blue-500/30 transition-all duration-300">
            <div className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6">
              <img 
                src="/favicon.svg?v=2" 
                alt="CRM Comparison" 
                className="w-full h-full filter drop-shadow-[0_0_4px_rgba(96,165,250,0.5)]"
                width="24"
                height="24"
                loading="eager"
              />
            </div>
            <span className="text-base sm:text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-300 to-cyan-200">
              Pipedrive vs HubSpot Comparison
            </span>
            <span className="hidden xs:inline-block text-sm text-slate-400">|</span>
            <span className="hidden xs:inline-block text-sm text-slate-300">
              Find Your Perfect CRM Match
            </span>
          </div>
        </div>

        {/* Hero Section with CTA */}
        <div className="relative max-w-6xl mx-auto px-4 py-16 sm:py-24 text-center">
          <div className="flex justify-center mb-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
              <img 
                src="/favicon.svg?v=2" 
                alt="CRM Comparison Tool" 
                className="w-full h-full animate-pulse duration-4000 filter drop-shadow-[0_0_8px_rgba(96,165,250,0.6)]"
                width="80"
                height="80"
                loading="eager"
              />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-50 mb-6 leading-tight">
            Find Your Perfect CRM Match
          </h1>
          <p className="text-base sm:text-xl text-slate-300 mb-10 sm:mb-14 max-w-2xl mx-auto px-2 leading-relaxed sm:leading-loose tracking-wide">
            Click the button below to discover whether{' '}
            <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-blue-200 to-blue-300 px-1">
              Pipedrive
            </span>{' '}
            or{' '}
            <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-blue-200 to-blue-300 px-1">
              HubSpot
            </span>{' '}
            is the better fit for your business needs
          </p>
          <button
            onClick={() => {
              setShowQuiz(true);
              // Track CTA click
              try {
                if (typeof gtag === 'function') {
                  gtag('event', 'start_quiz', {
                    event_category: 'Engagement',
                    event_label: 'Hero CTA'
                  });
                }
              } catch (e) {
                console.error('Analytics error:', e);
              }
            }}
            className="group relative inline-flex items-center gap-2 px-5 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500 via-blue-600 to-cyan-500 text-white rounded-xl transition-all duration-500 font-semibold text-sm sm:text-lg shadow-[0_8px_32px_-4px_rgba(59,130,246,0.3)] hover:shadow-[0_8px_48px_-4px_rgba(59,130,246,0.45)] hover:scale-[1.02] overflow-hidden"
            aria-label="Start CRM comparison quiz"
          >
            Find Your Perfect CRM
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 flex justify-center animate-bounce">
            <ChevronDown className="w-8 h-8 text-slate-400" aria-hidden="true" />
          </div>
        </div>

        {/* Summary Section with improved visibility */}
        <div className="relative max-w-6xl mx-auto px-4 py-8 sm:py-12 z-10">
          <div className="bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10 shadow-[0_8px_32px_-4px_rgba(0,0,0,0.3)]">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-50 mb-6">
              Pipedrive vs HubSpot: Quick Comparison
              <div className="mt-2 w-20 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
            </h2>
            <div className="space-y-6 text-slate-200">
              <p className="leading-relaxed">
                Choosing between Pipedrive and HubSpot depends on your business needs. Pipedrive excels in sales-focused features with pricing from $24/user/month, while HubSpot offers a comprehensive solution starting free for basic features.
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-50 mb-3">Pipedrive Best For:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" aria-hidden="true" />
                      <span>Small to medium sales teams</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" aria-hidden="true" />
                      <span>Visual pipeline management</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" aria-hidden="true" />
                      <span>Straightforward sales processes</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-50 mb-3">HubSpot Best For:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" aria-hidden="true" />
                      <span>Growing companies needing scalability</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" aria-hidden="true" />
                      <span>Marketing automation requirements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" aria-hidden="true" />
                      <span>All-in-one business solution</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quiz Modal */}
        {showQuiz && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
            <div className="relative max-w-2xl w-full bg-slate-800/90 backdrop-blur-sm rounded-2xl shadow-[0_20px_70px_-15px_rgba(0,0,0,0.5)] p-4 sm:p-6 md:p-8 border border-white/20 overflow-hidden">
              <button
                onClick={() => {
                  setShowQuiz(false);
                  resetQuiz();
                }}
                className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white rounded-full border border-slate-600 transition-all duration-300 shadow-lg z-10"
                aria-label="Close quiz"
              >
                ×
              </button>
              <div className="absolute -top-2 -right-2 -left-2 h-1.5 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-500 rounded-t-2xl" />
              {!result ? (
                <>
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h1 className="text-xl sm:text-3xl font-bold text-slate-50">
                        Find Your Perfect CRM
                      </h1>
                      <span className="text-xs sm:text-sm font-medium text-blue-100 bg-slate-700/50 px-2 sm:px-3 py-1 rounded-full">
                        Question {currentQuestion + 1} of {questions.length}
                      </span>
                    </div>
                    <div className="w-full bg-slate-600/50 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-blue-600 to-cyan-500 h-1.5 rounded-full transition-all duration-500 ease-out"
                        style={{
                          width: `${((currentQuestion + 1) / questions.length) * 100}%`,
                        }}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div className="mb-8 flex flex-col sm:flex-row items-center sm:items-start gap-4">
                    <div className="mb-4 sm:mb-0">
                      {getIcon(questions[currentQuestion].id)}
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-2xl font-semibold text-slate-50 mb-4 sm:mb-6 text-center sm:text-left">
                        {questions[currentQuestion].text}
                      </h2>
                      <div className="space-y-3">
                        {questions[currentQuestion].options.map((option, index) => (
                          <button
                            key={index}
                            onClick={() => handleAnswer(index)}
                            className="w-full text-left px-4 sm:px-6 py-3 sm:py-4 rounded-lg border border-slate-600 hover:border-blue-400/50 hover:bg-gradient-to-r hover:from-slate-700/50 hover:to-slate-600/50 transition-all duration-300 flex items-center justify-between group relative overflow-hidden"
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-400/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" 
                              aria-hidden="true" />
                            <div className="relative flex items-center justify-between w-full">
                              <span className="text-slate-200 group-hover:text-white transition-colors duration-300">
                                {option.text}
                              </span>
                              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-400 transition-colors duration-300" aria-hidden="true" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-slate-50 mb-6">Your Results</h2>
                  <div className="mb-8">
                    <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 mb-4">
                      {result.software === 'pipedrive' ? 'Pipedrive' : 'HubSpot'}
                    </div>
                    <p className="text-slate-300">is the recommended CRM for your needs</p>
                  </div>
                  
                  <div className="space-y-4 mb-8">
                    <div className="p-4 bg-slate-700/30 rounded-lg">
                      <div className="text-sm text-slate-400 mb-2">Pipedrive Score</div>
                      <div className="text-2xl font-bold text-slate-50">{result.score.pipedrive}%</div>
                      <div className="w-full h-2 bg-slate-600/50 rounded-full mt-2 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
                          style={{ width: `${result.score.pipedrive}%` }}
                          aria-hidden="true"
                        ></div>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-700/30 rounded-lg">
                      <div className="text-sm text-slate-400 mb-2">HubSpot Score</div>
                      <div className="text-2xl font-bold text-slate-50">{result.score.hubspot}%</div>
                      <div className="w-full h-2 bg-slate-600/50 rounded-full mt-2 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full"
                          style={{ width: `${result.score.hubspot}%` }}
                          aria-hidden="true"
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4 mb-8">
                    {result.software === 'pipedrive' ? (
                      <a
                        href="https://aff.trypipedrive.com/8vir3qqmj715"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          try {
                            if (typeof gtag === 'function') {
                              gtag('event', 'click', {
                                'event_category': 'CTA',
                                'event_label': 'Pipedrive Signup',
                                'value': result.score.pipedrive
                              });
                            }
                          } catch (e) {
                            console.error('Analytics error:', e);
                          }
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-500 via-blue-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                      >
                        Try Pipedrive Free for 14 Days
                        <ArrowRight className="w-5 h-5" aria-hidden="true" />
                      </a>
                    ) : (
                      <a
                        href="https://www.hubspot.com/products/get-started"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          try {
                            if (typeof gtag === 'function') {
                              gtag('event', 'click', {
                                'event_category': 'CTA',
                                'event_label': 'HubSpot Signup',
                                'value': result.score.hubspot
                              });
                            }
                          } catch (e) {
                            console.error('Analytics error:', e);
                          }
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-500 via-blue-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                      >
                        Get Started with HubSpot Free
                        <ArrowRight className="w-5 h-5" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                  
                  <div className="flex justify-between gap-4">
                    <button
                      onClick={resetQuiz}
                      className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white rounded-xl font-semibold transition-all duration-300"
                    >
                      Try Again
                    </button>
                    <button
                      onClick={() => setShowQuiz(false)}
                      className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white rounded-xl font-semibold transition-all duration-300"
                    >
                      View Comparison
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <ComparisonSection />
        <PricingSection />
        <FeaturesSection />
        <FAQSection />
        
        {/* Floating btoolme.com button */}
        <div className="fixed bottom-4 left-0 right-0 flex justify-center z-50">
          <a
            href="https://btoolme.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 hover:from-blue-500/20 hover:to-cyan-500/20 backdrop-blur-sm text-xs font-medium text-blue-300 hover:text-blue-200 rounded-full border border-blue-500/20 hover:border-blue-400/30 transition-all duration-300 shadow-[0_2px_10px_-2px_rgba(59,130,246,0.3)] hover:shadow-[0_4px_20px_-4px_rgba(59,130,246,0.5)]"
          >
            <span className="relative top-px">by btoolme.com</span>
            <ExternalLink className="w-3 h-3 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
          </a>
        </div>
      </div>
    </>
  );
}

export default App;